/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { noop } from 'lodash'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useDeleteCCMCluster, useGetCCMCluster } from '@modules/ccm-admin-lib/hooks'
import ClusterManagementPage from '@modules/ccm-components/cluster-page/ClusterPage'
import { connectedClustersURL } from '@modules/ccm-lib/urls'
import { addToast } from '@modules/cui/Toasts'
import { FormattedMessage } from '@modules/i18n'
import usePageTitle from '@modules/utils/usePageTitle'

import type { FC } from 'react'

const AdminCCMClusterManagementPage: FC = () => {
  const { clusterId } = useParams<{ clusterId: string }>()

  const getCCMClusterQuery = useGetCCMCluster(clusterId ?? '', {
    enabled: clusterId !== undefined,
  })

  const { data: ccmCluster, isSuccess: isFetchClusterSuccess } = getCCMClusterQuery

  usePageTitle(ccmCluster?.name ? `Connected cluster - ${ccmCluster.name}` : undefined)

  const clusterDisplayName = isFetchClusterSuccess ? ccmCluster.name : ''

  const { mutate: deleteCCMCluster } = useDeleteCCMClusterWithToasts(clusterDisplayName)

  return (
    <ClusterManagementPage
      canRenameCluster={false}
      onClickDisconnectCluster={(disconnectingClusterId: string) => {
        deleteCCMCluster({ id: disconnectingClusterId })
      }}
      onClickRenameCluster={noop}
      isSaving={false}
      {...getCCMClusterQuery}
    />
  )
}

export default AdminCCMClusterManagementPage

const useDeleteCCMClusterWithToasts = (clusterDisplayName: string) => {
  const navigate = useNavigate()

  return useDeleteCCMCluster({
    onSuccess: () => {
      addToast({
        title: (
          <FormattedMessage
            id='ccm.cluster-page.actions.disconnect-cluster.success'
            defaultMessage='Success'
          />
        ),
        color: 'success',
        iconType: 'check',
        text: (
          <FormattedMessage
            id='ccm.cluster-page.actions.disconnect-cluster.cluster-disconnected'
            defaultMessage='Cluster {clusterName} has been successfully disconnected'
            values={{ clusterName: clusterDisplayName }}
          />
        ),
      })

      navigate(connectedClustersURL())
    },
    onError: () => {
      addToast({
        title: (
          <FormattedMessage
            id='ccm.cluster-page.actions.disconnect-cluster.error'
            defaultMessage='Error'
          />
        ),
        color: 'danger',
        iconType: 'cross',
        text: (
          <FormattedMessage
            id='ccm.cluster-page.actions.disconnect-cluster.cluster-failed-to-disconnect'
            defaultMessage='Failed to disconnect cluster {clusterName}'
            values={{ clusterName: clusterDisplayName }}
          />
        ),
      })
    },
  })
}
